/**
 * gtp-buy-box.js
 *
 * Defines three custom elements for the Go-Tone Pro buy box:
 *  - <gtp-buy-box>    the tier selector / add-ons / add-to-cart control (also wires the
 *                     proof carousel's arrow buttons), rendered by sections/gtp-buy-box.liquid
 *  - <gtp-sticky-atc> the sticky bar rendered by sections/gtp-sticky-atc.liquid
 *  - <gtp-countdown>  the sale strip's live countdown, rendered by sections/gtp-buy-box.liquid
 *                     only while sale_mode is on
 *
 * They talk to each other and to assets/gtp-gallery.js purely through CustomEvents on
 * `document`:
 *   gtp:tier-change   { index, blockId, tierBlockId, imageIndex, variantId, title,
 *                        priceCents, compareAtCents, quantity }
 *   gtp:addons-change { variantIds, totalCents }
 *   gtp:cart-added    the raw /cart/add.js response
 *
 * All classes are defined defensively (customElements.get guard) because this file is
 * loaded with `defer` from both sections/gtp-buy-box.liquid and sections/gtp-sticky-atc.liquid,
 * and a Shopify page can render both sections at once.
 */

(function () {
  'use strict';

  /**
   * Formats an integer number of cents using a Shopify money_format string
   * (e.g. "${{amount}}", "{{amount_no_decimals}} kr"). Mirrors Shopify.formatMoney.
   */
  function gtpFormatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var moneyFormat = format || '${{amount}}';

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      number = number || 0;
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number === null) {
        return '0';
      }

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    var match = moneyFormat.match(placeholderRegex);
    if (!match) {
      return moneyFormat;
    }

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return moneyFormat.replace(placeholderRegex, value);
  }

  function gtpParseInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  var GTP_TIER_CHANGE = 'gtp:tier-change';
  var GTP_ADDONS_CHANGE = 'gtp:addons-change';
  var GTP_CART_ADDED = 'gtp:cart-added';

  /* -----------------------------------------------------------------------
     <gtp-buy-box>
     ----------------------------------------------------------------------- */

  function defineGtpBuyBox() {
    if (customElements.get('gtp-buy-box')) return;

    class GtpBuyBoxElement extends HTMLElement {
      /**
       * App embeds (Kaching Bundles) inject a <kaching-bundle> widget next to any
       * product form they find. Remove anything that is not ours from the
       * installments wrapper, now and whenever the app re-injects.
       */
      /** Country flag for the ships-free line, loaded at runtime from flagcdn. */
      loadFlags() {
        Array.prototype.slice.call(this.querySelectorAll('img[data-flag-iso]')).forEach(function (img) {
          var iso = (img.getAttribute('data-flag-iso') || '').toLowerCase();
          if (!/^[a-z]{2}$/.test(iso)) return;
          img.setAttribute('srcset', 'https://flagcdn.com/48x36/' + iso + '.png 2x');
          img.setAttribute('src', 'https://flagcdn.com/24x18/' + iso + '.png');
        });
      }

      /** Small info popovers (HSA/FSA). Toggle on click, close on outside click or Escape. */
      bindPopovers() {
        var self = this;
        var toggles = Array.prototype.slice.call(this.querySelectorAll('[data-gtp-popover-toggle]'));
        if (!toggles.length) return;
        var closeAll = function () {
          toggles.forEach(function (btn) {
            var pop = document.getElementById(btn.getAttribute('aria-controls'));
            if (pop) pop.hidden = true;
            btn.setAttribute('aria-expanded', 'false');
          });
        };
        toggles.forEach(function (btn) {
          btn.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            var pop = document.getElementById(btn.getAttribute('aria-controls'));
            if (!pop) return;
            var open = pop.hidden;
            closeAll();
            if (open) {
              pop.hidden = false;
              btn.setAttribute('aria-expanded', 'true');
            }
          });
        });
        document.addEventListener('click', function (event) {
          if (event.target.closest('[data-gtp-popover], [data-gtp-popover-toggle]')) return;
          closeAll();
        });
        document.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') closeAll();
        });
        self._closePopovers = closeAll;
      }

      /**
       * Accessory "See it in action" buttons ([data-gtp-video-trigger], one per addon block
       * with a video_id) share one dialog ([data-gtp-video-modal]). The dialog is moved to
       * <body> so position: fixed is never trapped by a transformed ancestor. The YouTube
       * iframe is created on open (muted, looped, no controls, nocookie host) and removed on
       * close so nothing loads until the shopper asks for it.
       */
      bindVideoPreviews() {
        var self = this;
        var modal = this.querySelector('[data-gtp-video-modal]');
        var triggers = Array.prototype.slice.call(this.querySelectorAll('[data-gtp-video-trigger]'));
        if (!modal || !triggers.length) return;

        document.body.appendChild(modal);
        var frame = modal.querySelector('[data-gtp-video-frame]');
        var titleEl = modal.querySelector('[data-gtp-video-title]');
        var closers = Array.prototype.slice.call(modal.querySelectorAll('[data-gtp-video-close]'));
        var opener = null;
        var prevOverflow = '';

        var close = function () {
          if (modal.hidden) return;
          modal.hidden = true;
          if (frame) frame.innerHTML = '';
          document.body.style.overflow = prevOverflow;
          if (opener && typeof opener.focus === 'function') opener.focus();
          opener = null;
        };

        var open = function (btn) {
          var id = (btn.getAttribute('data-video-id') || '').trim();
          if (!id || !frame) return;
          if (typeof self._closePopovers === 'function') self._closePopovers();
          opener = btn;
          if (titleEl) titleEl.textContent = btn.getAttribute('data-video-title') || '';
          frame.innerHTML = '';
          var iframe = document.createElement('iframe');
          var params = [
            'autoplay=1', 'mute=1', 'controls=0', 'loop=1', 'playlist=' + encodeURIComponent(id),
            'playsinline=1', 'modestbranding=1', 'rel=0', 'iv_load_policy=3'
          ].join('&');
          iframe.setAttribute('src', 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?' + params);
          iframe.setAttribute('title', btn.getAttribute('data-video-title') || '');
          iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
          iframe.setAttribute('allowfullscreen', '');
          frame.appendChild(iframe);
          prevOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          modal.hidden = false;
          var closeBtn = modal.querySelector('.gtp-bb__video-close');
          if (closeBtn) closeBtn.focus();
        };

        triggers.forEach(function (btn) {
          btn.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            open(btn);
          });
        });
        closers.forEach(function (el) {
          el.addEventListener('click', function (event) {
            event.preventDefault();
            close();
          });
        });
        document.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') close();
        });
        this._closeVideoPreview = close;
      }

      guardAppWidgets() {
        if (this.dataset.hideApps !== 'true') return;
        var wrap = this.querySelector('.gtp-bb__installments');
        var isForeign = function (node) {
          if (node.nodeType !== 1) return false;
          if (node.tagName === 'FORM' || node.tagName === 'SHOPIFY-PAYMENT-TERMS') return false;
          return true;
        };
        var sweep = function (root) {
          if (!root) return;
          Array.prototype.slice.call(root.children).forEach(function (child) {
            if (isForeign(child)) child.remove();
          });
        };
        sweep(wrap);
        Array.prototype.slice.call(this.querySelectorAll('kaching-bundle, [class^="kaching-bundles"]')).forEach(function (n) { n.remove(); });
        if (wrap && 'MutationObserver' in window) {
          var self = this;
          this.appGuardObserver = new MutationObserver(function () {
            sweep(wrap);
            Array.prototype.slice.call(self.querySelectorAll('kaching-bundle')).forEach(function (n) { n.remove(); });
          });
          this.appGuardObserver.observe(wrap, { childList: true });
        }
      }

      connectedCallback() {
        if (this.dataset.kachingTest === 'true') {
          this.loadFlags();
          this.bindPopovers();
          return;
        }
        this.guardAppWidgets();
        this.loadFlags();
        this.bindPopovers();
        this.bindVideoPreviews();
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.saleMode = this.dataset.saleMode === 'true';
        this.afterAdd = this.dataset.afterAdd || 'cart';
        this.cartUrl = this.dataset.cartUrl;
        this.cartAddUrl = this.dataset.cartAddUrl;

        this.tierRadios = Array.prototype.slice.call(this.querySelectorAll('[data-gtp-tier]'));
        this.addonRows = Array.prototype.slice.call(this.querySelectorAll('[data-gtp-addon]'));
        this.tierBoxes = document.querySelectorAll('[data-gtp-tier-box]');
        this.saleGiftLines = Array.prototype.slice.call(this.querySelectorAll('.gtp-bb__sale-gift-line'));

        this.priceEl = this.querySelector('[data-gtp-price]');
        this.compareEl = this.querySelector('[data-gtp-compare]');
        this.saveEl = this.querySelector('[data-gtp-save]');

        // There can be two add-to-cart rows (one under the tiers, one under the add-ons).
        // Every state change is applied to all of them; the singular refs point at the first.
        var all = function (root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };
        this.atcButtons = all(this, '[data-gtp-atc]');
        this.atcTextEls = all(this, '[data-gtp-atc-text]');
        this.totalEls = all(this, '[data-gtp-total]');
        this.spinnerEls = all(this, '[data-gtp-spinner]');
        this.errorEls = all(this, '[data-gtp-error]');
        this.atcButton = this.atcButtons[0] || null;
        this.atcTextEl = this.atcTextEls[0] || null;
        this.totalEl = this.totalEls[0] || null;
        this.spinnerEl = this.spinnerEls[0] || null;
        this.errorEl = this.errorEls[0] || null;

        this.addonsContainer = this.querySelector('[data-gtp-addons]');
        this.addonsMoreContainer = this.querySelector('[data-gtp-addons-more]');
        this.viewMoreButton = this.querySelector('[data-gtp-view-more]');
        this.viewMoreTextEl = this.querySelector('[data-gtp-view-more-text]');
        this.viewMoreCountOneTemplate = this.viewMoreButton ? this.viewMoreButton.dataset.viewMoreCountOneTemplate : '%COUNT% more accessory';
        this.viewMoreCountOtherTemplate = this.viewMoreButton ? this.viewMoreButton.dataset.viewMoreCountOtherTemplate : '%COUNT% more accessories';
        this.addonMaxQty = gtpParseInt(this.dataset.addonMaxQty, 5);

        this.proofTrack = this.querySelector('[data-gtp-proof-track]');
        this.proofPrevButton = this.querySelector('[data-gtp-proof-prev]');
        this.proofNextButton = this.querySelector('[data-gtp-proof-next]');

        this.bnplAmountEl = this.querySelector('[data-gtp-bnpl-amount]');
        this.bnplCount = gtpParseInt(this.dataset.bnplCount, 0);

        this.boxDetailsEl = this.querySelector('[data-gtp-box-details]');
        this.boxCountEl = this.querySelector('[data-gtp-box-count]');
        this.itemsCountOneTemplate = this.boxDetailsEl ? this.boxDetailsEl.dataset.itemsCountOneTemplate : '%COUNT% item';
        this.itemsCountOtherTemplate = this.boxDetailsEl ? this.boxDetailsEl.dataset.itemsCountOtherTemplate : '%COUNT% items';

        this.atcDefaultText = this.atcTextEl ? this.atcTextEl.textContent : 'Add to cart';
        this.soldOutText = this.dataset.soldOutText || 'Sold out';
        this.addingText = this.dataset.addingText || 'Adding...';
        this.errorText = this.dataset.errorText || 'Something went wrong. Please try again.';
        this.viewMoreText = this.dataset.viewMoreText || (this.viewMoreButton ? this.viewMoreButton.textContent : 'View more');
        this.viewLessText = this.dataset.viewLessText || 'View less';
        this.saveTemplate = this.dataset.saveTemplate || 'Save %AMOUNT%';
        this.expanded = false;

        this._state = {
          tierPriceCents: 0,
          tierVariantId: null,
          tierQuantity: 1,
          tierKey: null,
          tierTitle: '',
          addonVariantIds: [],
          totalCents: 0
        };

        this._onTierChange = this.handleTierRadioChange.bind(this);
        this._onAddonRowClick = this.handleAddonRowClick.bind(this);

        this.tierRadios.forEach(function (radio) {
          radio.addEventListener('change', this._onTierChange);
        }, this);

        this.addonRows.forEach(function (row) {
          row.addEventListener('click', this._onAddonRowClick);
        }, this);

        if (this.viewMoreButton) {
          this.viewMoreButton.addEventListener('click', this.toggleViewMore.bind(this));
        }

        if (this.proofPrevButton) {
          this.proofPrevButton.addEventListener('click', this.scrollProof.bind(this, -1));
        }
        if (this.proofNextButton) {
          this.proofNextButton.addEventListener('click', this.scrollProof.bind(this, 1));
        }

        var onAtc = this.handleAddToCart.bind(this);
        this.atcButtons.forEach(function (btn) { btn.addEventListener('click', onAtc); });

        var checkedRadio = this.tierRadios.filter(function (r) {
          return r.checked;
        })[0] || this.tierRadios[0];

        if (checkedRadio) {
          this.applyTier(checkedRadio, { silent: true });
        } else {
          this.recalcTotal();
        }
      }

      formatMoney(cents) {
        return gtpFormatMoney(cents, this.moneyFormat);
      }

      handleTierRadioChange(event) {
        this.applyTier(event.target, { silent: false });
      }

      applyTier(radio, options) {
        options = options || {};
        var self = this;

        var tierKey = radio.dataset.tierKey || null;
        var priceCents = gtpParseInt(radio.dataset.priceCents, 0);
        var compareCents = gtpParseInt(radio.dataset.compareCents, 0);
        var quantity = gtpParseInt(radio.dataset.quantity, 1);
        var available = radio.dataset.available !== 'false';
        var hasImage = radio.dataset.hasImage === 'true';
        var imageIndex = hasImage ? gtpParseInt(radio.dataset.imageIndex, -1) : -1;

        this._state.tierPriceCents = priceCents;
        this._state.tierVariantId = radio.dataset.variantId;
        this._state.tierComponents = null;
        if (radio.dataset.components) {
          try {
            var parsedComponents = JSON.parse(radio.dataset.components);
            if (Array.isArray(parsedComponents) && parsedComponents.length) this._state.tierComponents = parsedComponents;
          } catch (e) {
            this._state.tierComponents = null;
          }
        }
        this._state.tierQuantity = quantity;
        this._state.tierKey = tierKey;
        this._state.tierTitle = radio.dataset.title || '';
        this._state.tierBlockId = radio.dataset.blockId;
        this._state.tierAvailable = available;

        if (this.priceEl) {
          this.priceEl.textContent = this.formatMoney(priceCents);
        }

        if (compareCents > priceCents) {
          if (this.compareEl) {
            this.compareEl.textContent = this.formatMoney(compareCents);
            this.compareEl.hidden = false;
          }
          if (this.saveEl) {
            this.saveEl.textContent = this.saveTemplate.replace('%AMOUNT%', this.formatMoney(compareCents - priceCents));
            this.saveEl.hidden = false;
          }
        } else {
          if (this.compareEl) this.compareEl.hidden = true;
          if (this.saveEl) this.saveEl.hidden = true;
        }

        if (this.bnplAmountEl && this.bnplCount) {
          var bnplAmountCents = Math.floor(priceCents / this.bnplCount);
          this.bnplAmountEl.textContent = this.formatMoney(bnplAmountCents);
        }

        this.tierBoxes.forEach(function (box) {
          box.hidden = box.getAttribute('data-gtp-tier-box') !== radio.dataset.blockId;
        });

        // Item count chip on the "What's in the box" summary: scoped to the box-details
        // card only (not the "What's included" accordion's own copy of the same
        // data-gtp-tier-box markup), counted live from the now-visible list's lines.
        if (this.boxCountEl && this.boxDetailsEl) {
          var activeBox = this.boxDetailsEl.querySelector('[data-gtp-tier-box="' + radio.dataset.blockId + '"]');
          if (activeBox) {
            var itemCount = activeBox.querySelectorAll('.gtp-box-line').length;
            var itemsTemplate = itemCount === 1 ? this.itemsCountOneTemplate : this.itemsCountOtherTemplate;
            this.boxCountEl.textContent = itemsTemplate.replace('%COUNT%', itemCount);
          }
        }

        this.saleGiftLines.forEach(function (line) {
          line.hidden = line.getAttribute('data-tier-key') !== tierKey;
        });

        this.addonRows.forEach(function (row) {
          var tiers = row.getAttribute('data-tiers');
          var matches = tiers === 'both' || tiers === tierKey;
          row.hidden = !matches;
          if (!matches && row.getAttribute('data-gift') !== 'true') {
            self.updateAddonRowDOM(row, 0);
          }
        });

        this.updateViewMoreCount();

        this.atcButtons.forEach(function (btn) {
          btn.disabled = !available;
          btn.classList.toggle('gtp-bb__atc--soldout', !available);
        });
        var soldText = available ? this.atcDefaultText : this.soldOutText;
        this.atcTextEls.forEach(function (el) { el.textContent = soldText; });

        document.dispatchEvent(
          new CustomEvent(GTP_TIER_CHANGE, {
            detail: {
              userInitiated: !options.silent,
              index: this.tierRadios.indexOf(radio),
              blockId: radio.dataset.blockId,
              tierBlockId: !options.silent && hasImage ? radio.dataset.blockId : null,
              imageIndex: imageIndex,
              variantId: radio.dataset.variantId,
              title: radio.dataset.title,
              priceCents: gtpParseInt(radio.dataset.priceCents, 0),
              compareAtCents: gtpParseInt(radio.dataset.compareCents, 0),
              quantity: gtpParseInt(radio.dataset.quantity, 1)
            }
          })
        );

        this.recalcTotal();
      }

      recalcTotal() {
        var total = this._state.tierPriceCents || 0;
        var variantIds = [];
        var addonItems = [];
        var tierKey = this._state.tierKey;

        this.addonRows.forEach(function (row) {
          if (row.hidden) return;
          var isGift = row.getAttribute('data-gift') === 'true';
          var qty = gtpParseInt(row.getAttribute('data-qty'), 0);

          if (!isGift && qty <= 0) return;

          var variantId = row.getAttribute('data-variant-id');
          var unitPriceCents = gtpParseInt(row.getAttribute('data-price-cents'), 0);

          // Gift rows (sale mode) carry a different quantity per tier, e.g. the Dual
          // tier's free carry case is 2 units against the Single tier's 1, regardless of
          // the row's own data-qty.
          var lineQty = qty;
          if (isGift) {
            var qtyAttr = tierKey === 'dual' ? 'data-gift-qty-dual' : 'data-gift-qty-single';
            lineQty = gtpParseInt(row.getAttribute(qtyAttr), 1);
          }

          total += unitPriceCents * lineQty;
          variantIds.push(variantId);
          addonItems.push({ id: variantId, quantity: lineQty });
        });

        this._state.totalCents = total;
        this._state.addonVariantIds = variantIds;
        this._state.addonItems = addonItems;

        var totalText = this.formatMoney(total);
        this.totalEls.forEach(function (el) { el.textContent = totalText; });

        document.dispatchEvent(
          new CustomEvent(GTP_ADDONS_CHANGE, {
            detail: {
              variantIds: variantIds,
              totalCents: total,
              items: addonItems
            }
          })
        );
      }

      /**
       * Updates one addon row's DOM (data-qty, is-selected, add button vs. stepper
       * visibility, the qty text and the "x{qty}" price suffix) without recalculating
       * the cart total. Callers that need the total refreshed call recalcTotal()
       * themselves afterwards (setAddonQty does this for a single row; applyTier batches
       * several rows and recalculates once at the end).
       */
      updateAddonRowDOM(row, qty) {
        var max = gtpParseInt(this.addonMaxQty, 5);
        qty = Math.max(0, Math.min(qty, max));

        row.setAttribute('data-qty', String(qty));
        row.classList.toggle('is-selected', qty > 0);

        var addBtn = row.querySelector('[data-gtp-addon-add]');
        var stepper = row.querySelector('[data-gtp-addon-stepper]');
        var qtyEl = row.querySelector('[data-gtp-addon-qty]');
        var suffixEl = row.querySelector('[data-gtp-addon-qty-suffix]');
        var badgeEl = row.querySelector('[data-gtp-addon-badge]');

        if (addBtn) addBtn.hidden = qty > 0;
        if (stepper) stepper.hidden = qty === 0;
        if (qtyEl) qtyEl.textContent = String(qty);

        if (suffixEl) {
          if (qty > 1) {
            suffixEl.hidden = false;
            suffixEl.textContent = 'x' + qty;
          } else {
            suffixEl.hidden = true;
            suffixEl.textContent = '';
          }
        }

        if (badgeEl && row.getAttribute('data-gift') !== 'true') {
          badgeEl.hidden = qty === 0;
        }

        return qty;
      }

      /**
       * Sets one addon row's quantity and recalculates the cart total. Used by the
       * add / stepper / row-click handlers (never by the tier-switch reset, which
       * batches its resets through updateAddonRowDOM directly, see applyTier).
       */
      setAddonQty(row, qty) {
        this.updateAddonRowDOM(row, qty);
        this.recalcTotal();
      }

      /**
       * One delegated click listener per row handles the add button, both stepper
       * buttons, and a click on the row body itself (which only acts when the row is
       * currently unselected, per the buy box spec: clicking the row while selected does
       * nothing, only the stepper changes qty). The plus/minus/add controls are real
       * <button>s, so Enter/Space activate them natively and arrive here as a click.
       */
      handleAddonRowClick(event) {
        var row = event.currentTarget;

        if (row.getAttribute('data-gift') === 'true') return;
        if (row.getAttribute('data-available') === 'false') return;

        var qty = gtpParseInt(row.getAttribute('data-qty'), 0);
        var minusBtn = event.target.closest ? event.target.closest('[data-gtp-addon-minus]') : null;
        var plusStepBtn = event.target.closest ? event.target.closest('[data-gtp-addon-plus-step]') : null;
        var addBtn = event.target.closest ? event.target.closest('[data-gtp-addon-add]') : null;

        // "See it in action" opens the video dialog; it must never add the row to the cart.
        if (event.target.closest && event.target.closest('[data-gtp-video-trigger]')) return;

        if (minusBtn) {
          this.setAddonQty(row, qty <= 1 ? 0 : qty - 1);
          return;
        }
        if (plusStepBtn) {
          this.setAddonQty(row, qty + 1);
          return;
        }
        if (addBtn) {
          this.setAddonQty(row, 1);
          return;
        }
        if (qty === 0) {
          this.setAddonQty(row, 1);
        }
      }

      /**
       * Recomputes, for the currently selected tier, how many rows inside
       * .gtp-bb__addons-more are relevant (data-tiers is "both" or the active tier key),
       * updates the View more button's pluralised count label, and hides the button and
       * the peek container entirely when there is nothing to show for this tier.
       */
      updateViewMoreCount() {
        if (!this.addonsMoreContainer || !this.viewMoreButton) return;

        var tierKey = this._state.tierKey;
        var rows = Array.prototype.slice.call(this.addonsMoreContainer.querySelectorAll('[data-gtp-addon]'));
        var count = rows.filter(function (row) {
          var tiers = row.getAttribute('data-tiers');
          return tiers === 'both' || tiers === tierKey;
        }).length;

        this.viewMoreHiddenCount = count;

        if (count === 0) {
          this.expanded = false;
          this.addonsMoreContainer.setAttribute('data-expanded', 'false');
          this.addonsMoreContainer.hidden = true;
          this.viewMoreButton.hidden = true;
          this.viewMoreButton.setAttribute('aria-expanded', 'false');
          this.viewMoreButton.classList.remove('is-expanded');
          if (this.addonsContainer) this.addonsContainer.setAttribute('data-expanded', 'false');
          return;
        }

        this.addonsMoreContainer.hidden = false;
        this.viewMoreButton.hidden = false;

        if (this.viewMoreTextEl && !this.expanded) {
          var template = count === 1 ? this.viewMoreCountOneTemplate : this.viewMoreCountOtherTemplate;
          this.viewMoreTextEl.textContent = template.replace('%COUNT%', count);
        }
      }

      toggleViewMore() {
        this.expanded = !this.expanded;

        if (this.addonsContainer) {
          this.addonsContainer.setAttribute('data-expanded', this.expanded ? 'true' : 'false');
        }
        if (this.addonsMoreContainer) {
          this.addonsMoreContainer.setAttribute('data-expanded', this.expanded ? 'true' : 'false');
        }
        if (this.viewMoreButton) {
          this.viewMoreButton.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
        }
        if (this.viewMoreTextEl) {
          if (this.expanded) {
            this.viewMoreTextEl.textContent = this.viewLessText;
          } else {
            this.updateViewMoreCount();
          }
        }
      }

      /**
       * Scrolls the proof carousel track by one card's width (plus its gap) in the
       * given direction (-1 previous, 1 next).
       */
      scrollProof(direction) {
        if (!this.proofTrack) return;
        var card = this.proofTrack.querySelector('[data-gtp-proof-card]');
        var amount = card ? card.getBoundingClientRect().width + 10 : 300;
        this.proofTrack.scrollBy({ left: amount * direction, behavior: 'smooth' });
      }

      setLoading(isLoading) {
        if (!this.atcButtons.length) return;
        var disabled = isLoading || this._state.tierAvailable === false;
        this.atcButtons.forEach(function (btn) {
          btn.disabled = disabled;
          btn.classList.toggle('gtp-bb__atc--loading', isLoading);
        });
        this.spinnerEls.forEach(function (el) { el.hidden = !isLoading; });
        var text = isLoading ? this.addingText : this.atcDefaultText;
        this.atcTextEls.forEach(function (el) { el.textContent = text; });
      }

      showError(message) {
        this.errorEls.forEach(function (el) {
          el.textContent = message;
          el.hidden = false;
        });
      }

      clearError() {
        this.errorEls.forEach(function (el) {
          el.hidden = true;
          el.textContent = '';
        });
      }

      handleAddToCart(event) {
        event.preventDefault();

        if (!this._state.tierVariantId || this._state.tierAvailable === false) {
          return;
        }

        this.clearError();
        this.setLoading(true);

        var items = [];
        if (this._state.tierComponents) {
          this._state.tierComponents.forEach(function (comp) {
            items.push({ id: gtpParseInt(comp.id, 0), quantity: comp.quantity || 1 });
          });
        } else {
          items.push({
            id: gtpParseInt(this._state.tierVariantId, 0),
            quantity: this._state.tierQuantity || 1
          });
        }

        (this._state.addonItems || []).forEach(function (item) {
          items.push({ id: gtpParseInt(item.id, 0), quantity: item.quantity || 1 });
        });

        var config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: items })
        };

        var self = this;

        fetch(this.cartAddUrl, config)
          .then(function (response) {
            return response.json();
          })
          .then(function (response) {
            self.setLoading(false);

            if (response.status) {
              self.showError(response.description || response.message || self.errorText);
              return;
            }

            if (window.GTP_CART_OPEN) {
              window.GTP_CART_OPEN();
              return;
            }

            document.dispatchEvent(new CustomEvent(GTP_CART_ADDED, { detail: response }));

            try {
              if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
                window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                  source: 'gtp-buy-box',
                  productVariantId: self._state.tierVariantId
                });
              }
            } catch (e) {
              /* pub/sub is a nice-to-have; never block the cart flow on it */
            }

            if (self.afterAdd === 'drawer' && typeof window.SLIDECART_OPEN === 'function') {
              window.SLIDECART_OPEN();
            } else {
              window.location = self.cartUrl;
            }
          })
          .catch(function () {
            self.setLoading(false);
            self.showError(self.errorText);
          });
      }
    }

    customElements.define('gtp-buy-box', GtpBuyBoxElement);
  }

  /* -----------------------------------------------------------------------
     <gtp-sticky-atc>
     ----------------------------------------------------------------------- */

  function defineGtpStickyAtc() {
    if (customElements.get('gtp-sticky-atc')) return;

    class GtpStickyAtcElement extends HTMLElement {
      connectedCallback() {
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.titleEl = this.querySelector('[data-gtp-sticky-title]');
        this.priceEl = this.querySelector('[data-gtp-sticky-price]');
        this.button = this.querySelector('[data-gtp-sticky-button]');

        this._onTierChange = this.handleTierChange.bind(this);
        document.addEventListener(GTP_TIER_CHANGE, this._onTierChange);

        if (this.button) {
          this.button.addEventListener('click', this.scrollToBuyBox.bind(this));
        }

        this.setupObserver();

        // Do not rely solely on catching <gtp-buy-box>'s initial gtp:tier-change dispatch:
        // custom element upgrade order means that event can fire before this element's
        // own listener above is attached. Read the checked tier radio directly so the
        // sticky bar's price is never blank on load.
        this.syncFromCheckedTier();
      }

      syncFromCheckedTier() {
        var checkedRadio = document.querySelector('[data-gtp-tier]:checked');
        if (!checkedRadio) return;

        if (this.titleEl && checkedRadio.dataset.title) {
          this.titleEl.textContent = checkedRadio.dataset.title;
        }
        if (this.priceEl) {
          this.priceEl.textContent = this.formatMoney(gtpParseInt(checkedRadio.dataset.priceCents, 0));
        }
      }

      disconnectedCallback() {
        document.removeEventListener(GTP_TIER_CHANGE, this._onTierChange);
        if (this._observer) {
          this._observer.disconnect();
        }
      }

      formatMoney(cents) {
        return gtpFormatMoney(cents, this.moneyFormat);
      }

      handleTierChange(event) {
        var detail = event.detail || {};
        if (this.titleEl && detail.title) {
          this.titleEl.textContent = detail.title;
        }
        if (this.priceEl && typeof detail.priceCents === 'number') {
          this.priceEl.textContent = this.formatMoney(detail.priceCents);
        }
      }

      setupObserver() {
        var atcButton = document.querySelector('[data-gtp-atc]');
        if (!atcButton || !('IntersectionObserver' in window)) return;

        var self = this;

        this._observer = new IntersectionObserver(
          function (entries) {
            var entry = entries[0];
            if (!entry) return;

            if (entry.isIntersecting) {
              self.classList.remove('gtp-sticky--visible');
            } else if (entry.boundingClientRect.top < 0) {
              self.classList.add('gtp-sticky--visible');
            } else {
              self.classList.remove('gtp-sticky--visible');
            }
          },
          { threshold: 0, rootMargin: '0px 0px 0px 0px' }
        );

        this._observer.observe(atcButton);
      }

      scrollToBuyBox() {
        var atcButton = document.querySelector('[data-gtp-atc]');
        if (!atcButton) return;
        atcButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof atcButton.focus === 'function') {
          atcButton.focus({ preventScroll: true });
        }
      }
    }

    customElements.define('gtp-sticky-atc', GtpStickyAtcElement);
  }

  /* -----------------------------------------------------------------------
     <gtp-countdown>
     ----------------------------------------------------------------------- */

  function defineGtpCountdown() {
    if (customElements.get('gtp-countdown')) return;

    function pad2(n) {
      return n < 10 ? '0' + n : String(n);
    }

    class GtpCountdownElement extends HTMLElement {
      connectedCallback() {
        this.render();
        var self = this;
        this._interval = setInterval(function () {
          self.render();
        }, 1000);
      }

      disconnectedCallback() {
        if (this._interval) clearInterval(this._interval);
      }

      hideStrip() {
        this.hidden = true;
        var strip = this.closest('.gtp-bb__sale-strip');
        if (strip) strip.hidden = true;
      }

      render() {
        var endsAt = this.dataset.ends;
        var target = endsAt ? new Date(endsAt) : null;

        if (!target || isNaN(target.getTime())) {
          this.hideStrip();
          return;
        }

        var diff = target.getTime() - Date.now();

        if (diff <= 0) {
          this.hideStrip();
          if (this._interval) clearInterval(this._interval);
          return;
        }

        var totalSeconds = Math.floor(diff / 1000);
        var days = Math.floor(totalSeconds / 86400);
        var hours = Math.floor((totalSeconds % 86400) / 3600);
        var minutes = Math.floor((totalSeconds % 3600) / 60);
        var seconds = totalSeconds % 60;

        var text = pad2(hours) + ':' + pad2(minutes) + ':' + pad2(seconds);
        if (days > 0) {
          text = days + 'd ' + text;
        }

        this.textContent = text;
      }
    }

    customElements.define('gtp-countdown', GtpCountdownElement);
  }

  defineGtpBuyBox();
  defineGtpStickyAtc();
  defineGtpCountdown();
})();
